/**
 * Fixture books shaped like Lab `getBooksForBackend` event groups.
 * Used by unit tests and Detail Book workstation demos; the list page loads
 * live books via CM-API `useNetExposureBooks`.
 */

export type BookType = 'My PT' | 'Total Book'

export type SportName =
  | 'All'
  | 'Cricket'
  | 'Soccer'
  | 'Tennis'
  | 'Horse Racing'
  | 'Greyhound Racing'

export interface HorseAmount {
  name: string
  amount: number
  /** Lab `horse.horse` selection id. */
  selectionId?: string
}

export interface NetExposureMarket {
  marketId: string
  marketName: string
  netExposure: number
  horse: HorseAmount[]
  /** Exchange match odds pulled aside as `matchOdds` on the event. */
  isMatchOdds?: boolean
  bookMakerforNetExposure?: boolean
  /** Premium BM row (Artemis merge). */
  bookmakerFlag?: boolean
  eventName?: string
  marketUniqueId?: string
  eventBetfairId?: string
  key?: 'PB'
}

export interface NetExposureEvent {
  _id: string
  sport: Exclude<SportName, 'All'>
  /** Live event — drives the In play KPI, same idea as Exposure. */
  inPlay?: boolean
  matchOdds?: NetExposureMarket
  data: NetExposureMarket[]
  dataPrem: NetExposureMarket[]
  matchOddsPrem?: NetExposureMarket
}

export const BOOK_TYPES: BookType[] = ['My PT', 'Total Book']

export const SPORTS: { name: SportName; value: SportName }[] = [
  { name: 'All', value: 'All' },
  { name: 'Cricket', value: 'Cricket' },
  { name: 'Soccer', value: 'Soccer' },
  { name: 'Tennis', value: 'Tennis' },
  { name: 'Horse Racing', value: 'Horse Racing' },
  { name: 'Greyhound Racing', value: 'Greyhound Racing' },
]

/** Sample open book — structure matches Backend-UI net-exposure grid. */
export const FIXTURE_EVENTS: NetExposureEvent[] = [
  {
    _id: 'India v Australia',
    sport: 'Cricket',
    inPlay: true,
    matchOdds: {
      marketId: '1.23456789',
      marketName: 'Match Odds',
      isMatchOdds: true,
      netExposure: 42500,
      horse: [
        { name: 'India', amount: -82000 },
        { name: 'Australia', amount: 38000 },
        { name: 'The Draw', amount: 12000 },
      ],
    },
    data: [
      {
        marketId: '1.23456789-1',
        marketName: 'Fancy - 6 Over Runs Ind',
        netExposure: 15500,
        horse: [
          { name: 'Yes', amount: -15500 },
          { name: 'No', amount: 9000 },
        ],
      },
      {
        marketId: '1.23456789#bm',
        marketName: 'Bookmaker',
        bookMakerforNetExposure: true,
        netExposure: 21000,
        horse: [
          { name: 'India', amount: -45000 },
          { name: 'Australia', amount: 22000 },
          { name: 'The Draw', amount: 8000 },
        ],
      },
    ],
    dataPrem: [
      {
        marketId: 'pb-mo-1',
        marketUniqueId: 'pb-mo-1',
        marketName: 'Match Odds (Bookmaker)',
        bookmakerFlag: true,
        eventName: 'India v Australia',
        eventBetfairId: 'evt-ind-aus',
        key: 'PB',
        netExposure: 18000,
        horse: [
          { name: 'India', amount: -32000 },
          { name: 'Australia', amount: 14000 },
          { name: 'The Draw', amount: 5000 },
        ],
      },
      {
        marketId: 'pb-fancy-1',
        marketUniqueId: 'pb-fancy-1',
        marketName: 'Session 10 Over',
        bookmakerFlag: false,
        eventName: 'Premium Fancy',
        eventBetfairId: 'evt-ind-aus',
        key: 'PB',
        netExposure: 0,
        horse: [],
      },
      {
        marketId: 'pb-fancy-2',
        marketUniqueId: 'pb-fancy-2',
        marketName: 'Fall of 1st Wicket',
        bookmakerFlag: false,
        eventName: 'India v Australia',
        eventBetfairId: 'evt-ind-aus',
        key: 'PB',
        netExposure: 6400,
        horse: [
          { name: 'Yes', amount: -6400 },
          { name: 'No', amount: 3100 },
        ],
      },
    ],
  },
  {
    _id: 'England v New Zealand',
    sport: 'Cricket',
    matchOdds: {
      marketId: '1.98765432',
      marketName: 'Match Odds',
      isMatchOdds: true,
      netExposure: -12000,
      horse: [
        { name: 'England', amount: 28000 },
        { name: 'New Zealand', amount: -41000 },
        { name: 'The Draw', amount: 6000 },
      ],
    },
    data: [
      {
        marketId: '1.98765432-2',
        marketName: 'Fancy - 15 Over Runs Eng',
        netExposure: 8900,
        horse: [
          { name: 'Yes', amount: -8900 },
          { name: 'No', amount: 4200 },
        ],
      },
    ],
    dataPrem: [],
  },
  {
    _id: 'Manchester City v Arsenal',
    sport: 'Soccer',
    inPlay: true,
    matchOdds: {
      marketId: '1.55550001',
      marketName: 'Match Odds',
      isMatchOdds: true,
      netExposure: 9500,
      horse: [
        { name: 'Man City', amount: -22000 },
        { name: 'Arsenal', amount: 11000 },
        { name: 'The Draw', amount: 4000 },
      ],
    },
    data: [],
    dataPrem: [
      {
        marketId: 'pb-soc-1x2',
        marketUniqueId: 'pb-soc-1x2',
        marketName: 'Match 1X2',
        bookmakerFlag: true,
        eventName: 'Manchester City v Arsenal',
        eventBetfairId: 'evt-mci-ars',
        key: 'PB',
        netExposure: 7200,
        horse: [
          { name: '1', amount: -15000 },
          { name: '2', amount: 7000 },
          { name: 'X', amount: 2800 },
        ],
      },
    ],
  },
  {
    _id: 'Djokovic v Alcaraz',
    sport: 'Tennis',
    matchOdds: {
      marketId: '1.77770001',
      marketName: 'Match Odds',
      isMatchOdds: true,
      netExposure: 3100,
      horse: [
        { name: 'Djokovic', amount: -9000 },
        { name: 'Alcaraz', amount: 5800 },
      ],
    },
    data: [],
    dataPrem: [],
  },
]

export function eventNames(events: NetExposureEvent[]): string[] {
  return events.map((event) => event._id).sort((a, b) => a.localeCompare(b))
}

export type NeSort = 'worst' | 'best' | 'event'

export const NE_SORTS: { id: NeSort; label: string }[] = [
  { id: 'worst', label: 'Worst case first' },
  { id: 'best', label: 'Best case first' },
  { id: 'event', label: 'Event name' },
]

/** Same magnitude steps as Exposure “Worse than”. */
export const WORSE_THAN_STEPS = [10_000, 50_000, 1_00_000, 5_00_000, 10_00_000]

export function eventWorstCase(event: NetExposureEvent): number {
  return eventMarkets(event).reduce((sum, market) => sum + Math.min(0, marketWorst(market)), 0)
}

export function eventBestCase(event: NetExposureEvent): number {
  return eventMarkets(event).reduce((sum, market) => sum + Math.max(0, marketBest(market)), 0)
}

export function filterEvents(
  events: NetExposureEvent[],
  sport: SportName,
  eventName: string | null,
  liveOnly = false,
  worseThan: number | null = null,
  sort: NeSort = 'worst',
): NetExposureEvent[] {
  let out = events.filter((event) => {
    if (sport !== 'All' && event.sport !== sport) return false
    if (eventName && event._id !== eventName) return false
    if (liveOnly && !event.inPlay) return false
    if (worseThan != null && worseThan > 0) {
      if (Math.abs(eventWorstCase(event)) < worseThan) return false
    }
    return true
  })

  out = [...out]
  if (sort === 'worst') {
    out.sort((a, b) => eventWorstCase(a) - eventWorstCase(b))
  } else if (sort === 'best') {
    out.sort((a, b) => eventBestCase(b) - eventBestCase(a))
  } else {
    out.sort((a, b) => a._id.localeCompare(b._id))
  }
  return out
}

/** Markets that carry a real book (skip Premium Fancy header stubs). */
export function eventMarkets(event: NetExposureEvent): NetExposureMarket[] {
  const out: NetExposureMarket[] = []
  if (event.matchOdds) out.push(event.matchOdds)
  out.push(...event.data)
  for (const market of event.dataPrem) {
    if (market.eventName === 'Premium Fancy' && market.horse.length === 0) continue
    out.push(market)
  }
  return out
}

export function isFancyMarket(market: NetExposureMarket): boolean {
  if (market.isMatchOdds || market.bookMakerforNetExposure || market.bookmakerFlag) return false
  return true
}

export function marketWorst(market: NetExposureMarket): number {
  if (market.horse.length === 0) {
    // Fancy / session: Lab NE cell is `netExposure * -1` (no runner book).
    return -market.netExposure
  }
  return Math.min(...market.horse.map((h) => h.amount))
}

export function marketBest(market: NetExposureMarket): number {
  if (market.horse.length === 0) return -market.netExposure
  return Math.max(...market.horse.map((h) => h.amount))
}

/**
 * Same roll-up as Exposure / `buildEventExposure`: independent markets add
 * their losses (and wins) rather than offsetting each other.
 */
export function summariseNetExposure(events: NetExposureEvent[]) {
  let worstCase = 0
  let bestCase = 0
  let liveWorst = 0
  let liveEvents = 0
  let markets = 0
  let fancyLines = 0

  for (const event of events) {
    const books = eventMarkets(event)
    let eventWorst = 0
    for (const market of books) {
      eventWorst += Math.min(0, marketWorst(market))
      bestCase += Math.max(0, marketBest(market))
      markets += 1
      if (isFancyMarket(market)) fancyLines += 1
    }
    worstCase += eventWorst
    if (event.inPlay) {
      liveWorst += eventWorst
      liveEvents += 1
    }
  }

  return {
    worstCase,
    bestCase,
    liveWorst,
    liveEvents,
    markets,
    fancyLines,
    events: events.length,
  }
}
